import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Chip, CircularProgress } from '@mui/material';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '../axiosConfig';
import { useTemperatureUnit } from '../TemperatureUnitContext';

const RoomSensorsWidget = ({ projectId, onSettings, onRemove }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const { temperatureUnit, convertTemperature } = useTemperatureUnit();

  useEffect(() => {
    if (projectId) {
      fetchRooms();
    }
  }, [projectId]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/rooms/${projectId}`);
      const roomsWithData = await Promise.all(
        response.data.map(async (room) => {
          try {
            const sensorResponse = await axiosInstance.get(`/api/rooms/${room.id}/latest`);
            return {
              ...room,
              sensorData: sensorResponse.data,
              sensorStatus: sensorResponse.data.message && 
                sensorResponse.data.message.includes("No se encontraron mediciones recientes") 
                ? 'offline' : 'online'
            };
          } catch (error) {
            return {
              ...room,
              sensorData: { temperature: 0, humidity: 0, co2: 0 },
              sensorStatus: 'offline'
            };
          }
        })
      );
      setRooms(roomsWithData);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColor = (type, value) => {
    switch (type) {
      case 'temperature':
        return value < 18 ? '#2196f3' : value <= 29 ? '#4caf50' : value <= 30 ? '#ff9800' : '#f44336';
      case 'humidity':
        return value < 30 ? '#2196f3' : value <= 50 ? '#4caf50' : value <= 60 ? '#ff9800' : '#f44336';
      case 'co2':
        return value < 800 ? '#2196f3' : value <= 1500 ? '#4caf50' : value <= 1800 ? '#ff9800' : '#f44336';
      default:
        return '#757575';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        minHeight: '300px'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexShrink: 0
      }}>
        <Typography variant="body2" color="text.secondary">
          {rooms.length} habitaciones
        </Typography>
      </Box>

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}>
        {rooms.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No se encontraron habitaciones para este proyecto
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {rooms.map((room) => (
              <Grid item xs={12} key={room.id}>
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 2,
                  backgroundColor: room.sensorStatus === 'offline' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {room.name}
                    </Typography>
                    <Chip 
                      label={room.sensorStatus === 'online' ? 'En Línea' : 'Desconectado'}
                      color={room.sensorStatus === 'online' ? 'success' : 'warning'}
                      size="small"
                      icon={room.sensorStatus === 'offline' ? <WarningIcon /> : null}
                    />
                  </Box>

                  {room.sensorStatus === 'offline' ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Sensor Desconectado
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ width: 50, height: 50, mx: 'auto' }}>
                            <CircularProgressbar
                              value={convertTemperature(room.sensorData.temperature)}
                              maxValue={30}
                              text={`${convertTemperature(room.sensorData.temperature).toFixed(1)}°`}
                              styles={buildStyles({
                                textSize: '10px',
                                pathColor: getColor('temperature', room.sensorData.temperature),
                                textColor: getColor('temperature', room.sensorData.temperature),
                              })}
                            />
                          </Box>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>Temperatura</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ width: 50, height: 50, mx: 'auto' }}>
                            <CircularProgressbar
                              value={room.sensorData.humidity}
                              maxValue={100}
                              text={`${room.sensorData.humidity}%`}
                              styles={buildStyles({
                                textSize: '10px',
                                pathColor: getColor('humidity', room.sensorData.humidity),
                                textColor: getColor('humidity', room.sensorData.humidity),
                              })}
                            />
                          </Box>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>Humedad</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ width: 50, height: 50, mx: 'auto' }}>
                            <CircularProgressbar
                              value={Math.min(room.sensorData.co2 / 20, 100)}
                              maxValue={100}
                              text={`${room.sensorData.co2}`}
                              styles={buildStyles({
                                textSize: '8px',
                                pathColor: getColor('co2', room.sensorData.co2),
                                textColor: getColor('co2', room.sensorData.co2),
                              })}
                            />
                          </Box>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>CO2</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ width: 50, height: 50, mx: 'auto' }}>
                            {room.sensorData.soil_moisture !== null ? (
                              <CircularProgressbar
                                value={room.sensorData.soil_moisture}
                                maxValue={100}
                                text={`${room.sensorData.soil_moisture}%`}
                                styles={buildStyles({
                                  textSize: '8px',
                                  pathColor: '#4caf50',
                                  textColor: '#4caf50',
                                })}
                              />
                            ) : (
                              <Box sx={{ 
                                width: 50, 
                                height: 50, 
                                borderRadius: '50%', 
                                border: '3px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.8)'
                              }}>
                                <Typography variant="caption" color="text.secondary">N/A</Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>Suelo</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default RoomSensorsWidget; 